import { AuthError, AuthTokens, AuthState, UserJwt } from "./types"
import { atom } from "jotai"
import { atomWithStorage, loadable } from "jotai/utils"
import { getExpiresAtSeconds } from "./utils"

// Persistent atoms (survive app restarts) - with async initialization
export const accessTokenAtom = atomWithStorage<string | null>(
  "condri_access_token",
  null,
  undefined,
  { getOnInit: true },
)
export const twoFactorTokenAtom = atomWithStorage<string | null>(
  "condri_two_factor_token",
  null,
  undefined,
  { getOnInit: true },
)
export const tokenExpiresAtAtom = atomWithStorage<number | null>(
  "condri_token_expires_at",
  null,
  undefined,
  { getOnInit: true },
)

export const userJwtAtom = atomWithStorage<UserJwt | null>(
  "condri_user_jwt",
  null,
  undefined,
  { getOnInit: true },
)

// Sync versions for React components - derived from async storage atoms
const accessTokenLoadableAtom = loadable(accessTokenAtom)
const userJwtLoadableAtom = loadable(userJwtAtom)
const tokenExpiresAtLoadableAtom = loadable(tokenExpiresAtAtom)
const twoFactorTokenLoadableAtom = loadable(twoFactorTokenAtom)

export const accessTokenSyncAtom = atom((get) => {
  const loadable = get(accessTokenLoadableAtom)
  return loadable.state === "hasData" ? loadable.data : null
})

export const userJwtSyncAtom = atom((get) => {
  const loadable = get(userJwtLoadableAtom)
  return loadable.state === "hasData" ? loadable.data : null
})

export const tokenExpiresAtSyncAtom = atom((get) => {
  const loadable = get(tokenExpiresAtLoadableAtom)
  return loadable.state === "hasData" ? loadable.data : null
})

export const twoFactorTokenSyncAtom = atom((get) => {
  const loadable = get(twoFactorTokenLoadableAtom)
  return loadable.state === "hasData" ? loadable.data : null
})

// Runtime atoms
export const isInitializingAtom = atom(true) // Start as true, set to false after initial auth check
export const isLoadingAtom = atom(false) // For login/register/logout operations
export const isOfflineAtom = atom(false)
export const authErrorAtom = atom<AuthError | null>(null)

// Manually managed authentication state (not computed)
export const isAuthenticatedAtom = atom(false)

// Computed atoms - using sync versions for React components
export const needsRefreshAtom = atom((get) => {
  // Check if token is expired and needs refresh
  const accessToken = get(accessTokenSyncAtom)
  const expiresAt = get(tokenExpiresAtSyncAtom)
  if (!accessToken || !expiresAt) return false
  // Need refresh if token expires in the next 5 minutes
  return getExpiresAtSeconds(expiresAt) <= 5 * 60
})

export const requiresTwoFactorAtom = atom((get) => {
  const twoFactorToken = get(twoFactorTokenSyncAtom)
  const accessToken = get(accessTokenSyncAtom)
  return twoFactorToken !== null && accessToken === null
})

export const authTokensAtom = atom((get): AuthTokens => {
  const access_token = get(accessTokenSyncAtom)
  const expires_at = get(tokenExpiresAtSyncAtom)
  return { access_token, expires_at }
})

export const authStateAtom = atom(
  (get): AuthState => ({
    user_jwt: get(userJwtSyncAtom),
    tokens: get(authTokensAtom),
    isInitializing: get(isInitializingAtom),
    isLoading: get(isLoadingAtom),
    isAuthenticated: get(isAuthenticatedAtom),
    requiresTwoFactor: get(requiresTwoFactorAtom),
    needsRefresh: get(needsRefreshAtom),
    isOffline: get(isOfflineAtom),
    error: get(authErrorAtom),
  }),
)
